// Thin typed fetch wrapper. Features' api.ts call this; it never imports
// features. Every call is cancellable (AbortController) and time-bounded so a
// stale request can't win a race or hang a screen forever.

import { apiErrorFromResponse, parseApiError } from './error-handler';

const DEFAULT_TIMEOUT_MS = 15_000;

export interface RequestOptions {
  /** Caller-owned signal (react-query passes one for cancel-on-unmount). */
  signal?: AbortSignal;
  /** Hard ceiling; aborts and surfaces as a `timeout` ApiError. */
  timeoutMs?: number;
  headers?: Record<string, string>;
}

// ── Expired-token recovery ────────────────────────────────────────────────
//
// The access token lives ~15 minutes. sso-client.ts silently refreshes it and
// writes the result to a module variable, which React never re-renders on — so
// any component holding a token it read at mount keeps sending that one long
// after it expired. On a two-hour call that meant every host action (ending
// the call, admitting a guest from the lobby, saving notes) started 401ing
// about fifteen minutes in, while a perfectly valid token sat in memory.
//
// Fixing it at each call site is whack-a-mole; there are a dozen and every new
// one is a fresh chance to forget. So it is handled once, here: a 401 triggers
// one refresh and one replay with the new token.
//
// Wired by the auth feature at startup rather than imported directly — this
// module must not import features (see the file header), and inverting the
// dependency also keeps it usable in tests without dragging in the SSO client.

type RefreshFn = () => Promise<boolean>;
type TokenFn = () => string | null;
type OwnTokenFn = (token: string) => boolean;

let refreshAuth: RefreshFn | null = null;
let readToken: TokenFn | null = null;
let isOwnToken: OwnTokenFn | null = null;

/** Installed once by the auth feature. Until then, 401s pass straight through
 *  exactly as they did before this existed. */
export function installAuthRecovery(
  refresh: RefreshFn,
  getToken: TokenFn,
  ownToken: OwnTokenFn,
): void {
  refreshAuth = refresh;
  readToken = getToken;
  isOwnToken = ownToken;
}

/** Merge a caller signal with our timeout signal so either can abort. */
function withTimeout(signal: AbortSignal | undefined, timeoutMs: number) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return { signal: controller.signal, cleanup: () => clearTimeout(timer) };
}

/**
 * Is this request one whose 401 a token refresh could plausibly fix?
 *
 * Only requests that carried a Bearer token WE issued from the SSO session.
 * Deliberately excluded:
 *
 *   - Requests with no Authorization header at all. Their 401 means "this
 *     endpoint needs auth and you sent none" — refreshing changes nothing, and
 *     retrying just doubles the traffic on every guest-path 401.
 *   - Requests whose Bearer is NOT the current SSO token. That is a
 *     Sales-embedded host's fragment token, which Meet cannot refresh (no
 *     refresh cookie for that session); retrying with the SSO token would
 *     silently swap one identity for another, which is worse than the 401.
 *   - Non-Bearer credentials (X-Meet-Service-Key and friends).
 */
function isRefreshableAuth(headers: Record<string, string> | undefined): boolean {
  if (!headers || !isOwnToken) return false;
  const key = Object.keys(headers).find((h) => h.toLowerCase() === 'authorization');
  if (!key) return false;
  const value = headers[key];
  if (!value?.toLowerCase().startsWith('bearer ')) return false;
  // "Ours" rather than "current": a refresh can land between a stale request
  // going out and its 401 coming back, and that request still deserves a
  // retry. The auth feature keeps a short recency window for exactly this.
  return isOwnToken(value.slice(7).trim());
}

async function request<T>(
  method: string,
  url: string,
  body: unknown,
  opts: RequestOptions = {},
  // Guarantees termination: the retry calls through with `false`, so a 401 on
  // the replay surfaces to the caller instead of starting the cycle again.
  // Without this an endpoint that 401s regardless of token would loop forever.
  allowRetry = true,
): Promise<T> {
  const { signal, cleanup } = withTimeout(opts.signal, opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...opts.headers,
      },
      // Re-serialised on the replay from the original `body` value, not from a
      // consumed stream — a Request body can only be read once, so retrying a
      // stream would send an empty payload.
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (
      res.status === 401 &&
      allowRetry &&
      refreshAuth &&
      isRefreshableAuth(opts.headers) &&
      // A caller who already gave up (unmounted component, cancelled query)
      // must not have work done on their behalf.
      !signal.aborted
    ) {
      // refresh() coalesces concurrent callers into one network call and holds
      // a cross-tab lock, so a burst of 401s from several parallel queries
      // rotates the cookie exactly once.
      const ok = await refreshAuth();
      if (ok && !opts.signal?.aborted) {
        const token = readToken?.();
        if (token) {
          const authKey =
            Object.keys(opts.headers ?? {}).find(
              (h) => h.toLowerCase() === 'authorization',
            ) ?? 'Authorization';
          return request<T>(
            method,
            url,
            body,
            { ...opts, headers: { ...opts.headers, [authKey]: `Bearer ${token}` } },
            false,
          );
        }
      }
    }

    if (!res.ok) throw await apiErrorFromResponse(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } catch (err) {
    throw parseApiError(err);
  } finally {
    cleanup();
  }
}

export const http = {
  get: <T>(url: string, opts?: RequestOptions) => request<T>('GET', url, undefined, opts),
  post: <T>(url: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', url, body, opts),
  patch: <T>(url: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', url, body, opts),
  del: <T>(url: string, opts?: RequestOptions) => request<T>('DELETE', url, undefined, opts),
};
